/**
 * Translation dictionaries — Sprint BK.
 *
 * Keys are dotted English identifiers (`nav.command`,
 * `usage.cap.soft`) rather than full English sentences. Two
 * reasons:
 *   1. Source-language drift. If we used the English sentence
 *      as the key, fixing a typo in en would silently break ru.
 *   2. Keys are searchable in code without false positives on
 *      every page that happens to mention the same word.
 *
 * `ru` is the default. The dictionaries are exhaustive for the
 * keys this sprint migrates; the `useT` hook surfaces a clear
 * `[missing: key]` placeholder when a string isn't translated,
 * which is visible at runtime and impossible to miss in PR
 * review.
 *
 * Adding a language: add a record under the same key shape and
 * register it in `LANGS` below.
 */

export type Lang = "ru" | "en";


export const LANGS: { code: Lang; label: string }[] = [
  { code: "ru", label: "Русский" },
  { code: "en", label: "English" },
];


type Dict = Record<string, string>;


const RU: Dict = {
  // ---- Common --------------------------------------------------------
  "common.refresh":       "Обновить",
  "common.save":          "Сохранить",
  "common.cancel":        "Отмена",
  "common.saving":        "Сохраняем…",
  "common.loading":       "Загрузка…",
  "common.no_data":       "Нет данных",
  "common.logout":        "Выйти",
  "common.logout.title":  "Выйти ({user})",
  "shell.notimpl.title":  "Раздел {path} ещё не подключён",
  "shell.notimpl.body":   "Будет добавлен в следующих спринтах. Сейчас работают: AI Command, Дашборд, Объявления, Диалоги, Клиенты, Запуски агентов.",
  "shell.ai.title":       "AI ассистент",
  "shell.ai.hint":        "Главный AI Command Center — слева в навигации (вкладка ✨)",
  "shell.ai.realtime":    "Realtime-чат с агентами появится в Sprint 6 (ws-gateway).",
  "shell.ai.usefor":      "Пока используйте AI Command для запуска задач.",
  "connectors.sync":      "Синхронизировать",
  "connectors.sync.started": "Запущено. correlation_id:",
  "connectors.history":   "История запусков",
  "connectors.history.empty": "Запусков ещё не было.",
  "connectors.history.col.status": "Статус",
  "connectors.history.col.started": "Запущен",
  "connectors.history.refresh": "Обновить историю",
  "connectors.badge.enabled":   "включён",
  "connectors.badge.disabled":  "выключен",
  "connectors.health.healthy":  "работает",
  "connectors.health.stale":    "давно не запускался",
  "connectors.health.degraded": "{n} сбой подряд",
  "connectors.health.failing":  "{n} сбоев подряд",
  "command.sample.1":     "нужен экскаватор 30т Hyundai в Краснодаре",
  "command.sample.2":     "сколько стоит аренда крана 25т",
  "command.sample.3":     "опубликуй объявление по моему JCB 3CX",
  "command.sample.4":     "помоги настроить Telegram-бота",
  "command.final_answer": "Итоговый ответ",
  "command.found":        "Найдено: {total}, показываю топ {shown}",
  "conv.sample.text":     "Сколько стоит экскаватор 30т на смену?",
  "conv.back":            "Назад",
  "conv.title":           "Диалог в {channel}",
  "conv.return_ai":       "Вернуть AI",
  "conv.take_over":       "Взять на себя",
  "customers.kv.created": "Создан",
  "customers.related":    "Связанные сущности",
  "customers.related.note": "Click через CRM Agent уже создаёт записи — просто пока нет join-страницы.",
  "states.loading":       "Загружаю...",
  "states.error.title":   "Ошибка загрузки",
  "states.error.retry":   "Повторить",
  "skeleton.aria.table":  "Загрузка таблицы",
  "skeleton.aria.stats":  "Загрузка статистики",
  "theme.option.light":   "Светлая",
  "theme.option.system":  "Системно",
  "theme.option.dark":    "Тёмная",
  "theme.aria":           "Тема оформления",
  "errboundary.title":    "Что-то пошло не так",
  "errboundary.scope":    " на странице {scope}",
  "errboundary.unknown":  "Неизвестная ошибка.",
  "errboundary.devnote":  "Информация для разработчиков скопирована в консоль браузера.",
  "errboundary.retry":    "Повторить",
  "errboundary.reload":   "Перезагрузить страницу",
  "pager.prev":           "← Назад",
  "pager.next":           "Вперёд →",
  "inbox.title":          "Inbox согласований",
  "inbox.subtitle":       "AI proposes — вы решаете. Live-обновление через WebSocket.",
  "inbox.proposed_by":    "от {agent} ·",
  "inbox.section.reasoning": "AI обоснование",
  "inbox.section.changes":   "Что изменится",
  "inbox.section.rollback":  "План отката",
  "inbox.section.decision":  "Решение",
  "inbox.editor.toggle":  "Редактировать payload",
  "inbox.editor.hint":    "Изменения пойдут как `modified_payload` агенту.",
  "inbox.editor.reset":   "Сбросить",
  "inbox.editor.error.object": "payload должен быть JSON-объектом",
  "inbox.editor.error.invalid": "невалидный JSON",
  "inbox.action.approve":          "Одобрить",
  "inbox.action.approve_modified": "Одобрить с изменениями",
  "inbox.action.reject":           "Отклонить",
  "inbox.chain.step":      "Шаг {n}",
  "inbox.chain.waiting":   "{label} · ждёт предыдущий",
  "inbox.chain.ready":     "{label} · готов к решению",
  "dashboard.header.date":  "10 мая 2026 · обновлено 14:23",
  "dashboard.kpi.trend.revenue":   "+12% к среднему",
  "dashboard.kpi.trend.published": "+2 новых",
  "dashboard.kpi.trend.active":    "все на ходу",
  "dashboard.kpi.trend.ai_cost":   "−18% к прошлой",
  "dashboard.demo.q1":       "нужен экскаватор 30т Краснодар на 2 недели",
  "dashboard.demo.region":   "Краснодар",
  "dashboard.demo.summary":  "Понял. Нашёл 14 вариантов, топ-3:",
  "dashboard.demo.l1.spec":  "18 000 ₽/смена · 4 км от объекта · свободен с 12.05",
  "dashboard.demo.l1.badge": "4.9 · 127 сделок",
  "dashboard.demo.l2.spec":  "17 500 ₽/смена · 12 км от объекта",
  "dashboard.demo.l2.badge": "4.8 · 89 сделок",
  "dashboard.demo.l3.spec":  "19 000 ₽/смена · 8 км от объекта",
  "dashboard.demo.l3.badge": "5.0 · доступен с 13.05",
  "dashboard.demo.book":     "Забронировать №1",
  "dashboard.demo.compare":  "Сравнить",
  "dashboard.demo.more":     "Показать ещё 11",
  "dashboard.demo.q2":       "забронируй №1 на 2 недели с 12 мая",
  "dashboard.demo.confirm":  "Готов забронировать Hyundai R300LC-9SH с 12.05 по 26.05 (14 смен × 18 000 ₽ = 252 000 ₽).",
  "dashboard.gate.title":    "Перевести 252 000 ₽ на номинальный счёт",
  "dashboard.gate.kv.deal":     "Сделка",
  "dashboard.gate.kv.supplier": "Поставщик",
  "dashboard.gate.kv.supplier.v": "ИП Петров (4.9★, 127 сделок)",
  "dashboard.gate.kv.period":   "Период",
  "dashboard.gate.kv.period.v": "12.05.2026 — 26.05.2026 · 14 смен",
  "dashboard.gate.kv.amount":   "Сумма",
  "dashboard.gate.kv.amount.v": "252 000 ₽ · НДС включён",
  "dashboard.gate.kv.deposit":  "Залог",
  "dashboard.gate.kv.deposit.v": "50 000 ₽ · возврат после возврата техники",
  "dashboard.gate.risk":     "Требуется подтверждение человека на любые операции с деньгами выше 50 000 ₽.",
  "dashboard.gate.primary":  "Подтвердить и оплатить",
  "dashboard.gate.secondary":"Изменить даты",
  "dashboard.gate.confidence_source": "реальные доступные слоты у поставщика",
  "dashboard.activity.all":     "Все",
  "dashboard.activity.item1":   "Posting Agent опубликовал «Hyundai R300» в Краснодаре. Уже 12 просмотров.",
  "dashboard.activity.item2":   "CRM Agent ответил клиенту по заявке #ZK-918 (категория: 30т).",
  "dashboard.activity.item3":   "Ждёт подтверждения: оплата 187 400 ₽ → ИП Петров (сделка #SD-9182).",
  "dashboard.activity.item3.approve": "Подтвердить",
  "dashboard.activity.item3.reject":  "Отклонить",
  "dashboard.activity.item4":   "Search Agent обработал 4 заявки за последний час.",
  "dashboard.map.open":     "Открыть",
  "dashboard.map.placeholder": "MapLibre будет в Sprint 4 (packages/maps)",
  "dashboard.map.available": "Доступна: 12",
  "dashboard.map.on_deal":   "На сделке: 3",
  "usage.cap.soft.label":  "Soft cap (USD)",
  "usage.cap.hard.label":  "Hard cap (USD)",
  "usage.events.unit.ms":  "мс",
  "listings.draft.title":  "Опубликовать через AI",
  "listings.draft.subtitle": "Photo URL → Posting Agent распознаёт + создаёт Listing",
  "listings.draft.url":    "URL фотографии",
  "listings.draft.hint":   "Подсказка (опционально)",
  "listings.draft.hint.placeholder": "экскаватор Hyundai R300 30т",
  "listings.draft.submit": "Запустить Posting Agent",
  "listings.empty.title":  "Объявлений ещё нет",
  "listings.empty.body":   "Загрузите URL фото выше — Posting Agent создаст первое.",
  "listings.col.title":    "Заголовок",
  "listings.col.type":     "Тип",
  "listings.col.status":   "Статус",
  "listings.col.photos":   "Фото",
  "listings.col.views":    "Просм.",
  "listings.col.created":  "Создано",
  "settings.desc.line1":      "Здесь вы вставляете API-ключи, bot-токены и базовые URL.",
  "settings.desc.line2.pre":  "Все секреты ",
  "settings.desc.line2.bold": "шифруются на стороне сервера",
  "settings.desc.line2.post": " перед записью в БД (envelope encryption, Sprint P).",
  "settings.feedback.saved":     "Сохранено. Секреты зашифрованы и записаны в БД.",
  "settings.confirm.uninstall":  "Удалить установку «{name}»? Все сохранённые ключи будут стёрты (потребуется ввести заново).",
  "settings.feedback.removed":   "Установка удалена.",
  "settings.badge.installed":    "установлен",
  "settings.badge.disabled":     "выключен",
  "settings.badge.not_installed":"не установлен",
  "settings.no_creds":           "Этот коннектор не требует учётных данных.",
  "settings.enabled":            "Включён",
  "settings.uninstall":          "Удалить установку",
  "settings.save_changes":       "Сохранить изменения",
  "settings.install":            "Установить",
  "settings.field.secret":       "(секрет)",
  "settings.field.filled":       "заполнено",
  "settings.field.placeholder.configured": "•••••••••• (оставьте пустым, чтобы не менять)",
  "cost.range.7":      "7д",
  "cost.range.30":     "30д",
  "cost.range.90":     "90д",
  "cost.range.365":    "365д",
  "cost.tile.spend":   "Расход",
  "cost.tile.spend.secondary":  "{calls} вызовов",
  "cost.tile.tokens":  "Токены",
  "cost.tile.tokens.secondary": "в среднем {avg}/вызов",
  "cost.tile.latency": "Латентность",
  "cost.tile.latency.value":    "{ms} мс",
  "cost.tile.latency.secondary":"среднее",
  "cost.tile.cache.good": "хорошо",
  "cost.tile.cache.low":  "низко",
  "cost.empty.period": "Нет данных за выбранный период.",
  "cost.chart.tip":    "{day}: ${cost} ({calls} вызовов)",
  "cost.chart.peak":   "пик: ${cost}",
  "cost.empty.short":  "нет данных",
  "vehicles.filter.all":         "Все",
  "vehicles.filter.idle":        "Простаивают",
  "vehicles.filter.busy":        "В работе",
  "vehicles.filter.maintenance": "Сервис",
  "vehicles.filter.offline":     "Offline",
  "vehicles.empty.short":  "нет данных",
  "vehicles.unit.kmh":     "км/ч",
  "vehicles.ago.just_now": "только что",
  "vehicles.ago.seconds":  "{s} с",
  "vehicles.ago.minutes":  "{m} мин",
  "vehicles.ago.hours":    "{h} ч",
  "vehicles.ago.days":     "{d} дн",
  "dispatch.map.aria":     "Карта диспетчера",

  // ---- Sidebar nav ---------------------------------------------------
  "nav.command":          "AI Command",
  "nav.inbox":            "Inbox",
  "nav.dashboard":        "Дашборд",
  "nav.listings":         "Объявления",
  "nav.customers":        "Клиенты",
  "nav.messages":         "Переписки",
  "nav.fleet":            "Парк",
  "nav.dispatch":         "Диспетчер",
  "nav.orders":           "Заявки",
  "nav.intake":           "Воронка intake",
  "nav.alerts":           "Тревоги",

  "intake.title":         "Воронка intake",
  "intake.subtitle":      "Поток сообщений из мессенджеров → классификатор → заявки",
  "intake.empty.title":   "Пока нет классификаций",
  "intake.empty.description": "Подключите Telegram-бот или WhatsApp — сообщения появятся здесь по мере поступления.",
  "intake.stat.total":    "Сообщений / 24ч",
  "intake.stat.orders":   "Стало заявками",
  "intake.stat.spam":     "Отфильтровано",
  "intake.stat.spam.hint":"спам / мета / болтовня",
  "intake.stat.latency":  "Среднее время",
  "intake.stat.latency.hint": "qwen + gpt",
  "intake.stat.cost":     "Стоимость LLM",
  "intake.stat.cost.hint":"за 24 часа",
  "alerts.page.title":    "Тревоги техники",
  "alerts.empty.title":   "Тревог нет",
  "alerts.empty.unack":   "Все тревоги обработаны. Молодцы.",
  "alerts.empty.any":     "Wialon ещё не зарегистрировал ни одной тревоги.",
  "alerts.tab.unack":     "Непросмотренные",
  "alerts.tab.acked":     "Обработанные",
  "alerts.tab.all":       "Все",
  "alerts.rule.geofence_exit": "Покинул гео-зону",
  "alerts.rule.idle_too_long": "Простой",
  "alerts.rule.fuel_drop":     "Падение топлива",
  "alerts.rule.speed_over":    "Превышение скорости",
  "alerts.row.vehicle":   "техника #{id}",
  "alerts.row.ack":       "Подтвердить",
  "alerts.row.acked_at":  "обработано {ts}",
  "alerts.details.zone":   "зона",
  "alerts.details.elapsed":"простой {mins} мин",
  "alerts.details.since":  "с",
  "orders.page.title":    "Заявки",
  "orders.empty.title":   "Заявок нет",
  "orders.empty.description": "Подключите ATI.SU или 1С — заявки появятся здесь после первого pull'a.",
  "orders.tab.all":           "Все",
  "orders.tab.new":           "Новые",
  "orders.tab.accepted":      "В работе",
  "orders.tab.in_progress":   "Идут",
  "orders.tab.completed":     "Завершены",
  "orders.status.new":         "новая",
  "orders.status.accepted":    "принята",
  "orders.status.declined":    "отклонена",
  "orders.status.in_progress": "в работе",
  "orders.status.completed":   "завершена",
  "orders.status.cancelled":   "отменена",
  "orders.detail.title":       "Заявка",
  "orders.detail.route":       "Маршрут",
  "orders.detail.sources":     "Источники ({n})",
  "orders.detail.metadata":    "Дополнительные данные",
  "orders.detail.kg":          "кг",
  "orders.detail.first_seen":  "впервые увидели",
  "orders.detail.last_seen":   "последний раз",
  "orders.detail.updated_at":  "обновлено {ts}",
  "orders.detail.terminal":    "Финальный статус — переходы недоступны",
  "nav.agent_runs":       "Запуски агентов",
  "nav.connectors":       "Коннекторы",
  "nav.cost":             "Стоимость AI",
  "nav.usage":            "Лимиты и расход",
  "nav.platform":         "Платформа",
  "nav.audit":            "Журнал аудита",
  "nav.analytics":        "Аналитика",
  "nav.settings":         "Настройки",
  "nav.privacy":          "Приватность",

  // ---- Privacy / consent --------------------------------------------
  "privacy.page.title":          "Конфиденциальность и согласие",
  "privacy.page.subtitle":       "Политика обработки персональных данных и ваши права согласно 152-ФЗ",
  "privacy.tile.consent":        "Согласие",
  "privacy.tile.consent.active": "Действует",
  "privacy.tile.consent.refresh":"Требуется обновление",
  "privacy.tile.consent.none":   "Не получено",
  "privacy.tile.deletion":       "Удаление аккаунта",
  "privacy.tile.deletion.none":  "Не запрошено",
  "privacy.tile.deletion.pending":"Запрошено",
  "privacy.action.accept":       "Принять политику",
  "privacy.action.delete":       "Удалить аккаунт",
  "privacy.action.cancel":       "Отменить удаление",
  "privacy.banner.text":         "Опубликована новая версия политики обработки персональных данных. Подтвердите согласие, чтобы продолжить пользоваться системой.",
  "privacy.banner.read":         "Прочитать",
  "privacy.banner.accept":       "Принять",
  "privacy.confirm.delete":      "Запросить удаление аккаунта?\n\nЛичные данные будут обезличены через 30 дней. До истечения этого срока вы можете отменить запрос.",
  "privacy.tile.consent.secondary":   "версия {version} · {date}",
  "privacy.tile.consent.secondary.noversion": "—",
  "privacy.tile.deletion.fires":      "обезличивание {date}",
  "privacy.tile.deletion.empty":      "Можете запросить ниже",
  "privacy.tile.version":             "Текущая версия политики",
  "privacy.tile.version.hint":        "ниже на странице",
  "privacy.tile.email":               "Email",
  "privacy.tile.email.verified":      "подтверждён",
  "privacy.tile.email.unverified":    "не подтверждён",

  // ---- Usage page ----------------------------------------------------
  "usage.page.title":     "Использование AI и лимиты",
  "usage.page.subtitle":  "Текущий период, расход, soft / hard cap",
  "usage.edit_cap":       "Изменить лимит",
  "usage.tile.spent":     "Расход за период",
  "usage.tile.spent.ok":  "в пределах",
  "usage.tile.spent.over_soft": "превышен soft cap",
  "usage.tile.spent.over_hard": "превышен hard cap",
  "usage.tile.soft":      "Soft cap",
  "usage.tile.soft.hint": "уведомление",
  "usage.tile.hard":      "Hard cap",
  "usage.tile.hard.hint": "блокирует вызовы",
  "usage.tile.period":    "Период",
  "usage.tile.period.hint": "UTC",
  "usage.cap.soft.hint":  "Только уведомление; не блокирует. Пусто = без soft cap.",
  "usage.cap.hard.hint":  "Блокирует AI-вызовы при превышении. Пусто = без hard cap.",
  "usage.cap.placeholder": "без лимита",
  "usage.forecast.label":      "Прогноз на конец месяца:",
  "usage.forecast.days":       "({elapsed} из {total} дней)",
  "usage.forecast.over_soft":  "превысит soft cap (${cap})",
  "usage.forecast.over_hard":  "превысит hard cap (${cap}) — AI-вызовы будут блокированы",
  "usage.forecast.day1_hint":  "(прогноз 1-го дня — шумный)",
  "usage.forecast.method.linear":     "(линейная экстраполяция)",
  "usage.forecast.method.median_7d":  "(медиана за 7 дней)",
  "usage.forecast.linear_was":        "линейный был ${value}",
  "settings.test_connection":         "Проверить подключение",
  "settings.test.ok":                 "✓ Подключение успешно",
  "settings.test.fail":               "✗ Ошибка подключения",
  "usage.forecast.anomaly.label":     "⚠ Обнаружен всплеск расходов сегодня",
  "usage.forecast.anomaly.today":     "сегодня: ${value}",
  "usage.forecast.anomaly.baseline":  "baseline (медиана недели): ${value}",
  "usage.events.title":   "Последние LLM-вызовы",
  "usage.events.col.time":   "Время",
  "usage.events.col.model":  "Provider · Model",
  "usage.events.col.task":   "Task",
  "usage.events.col.tokens": "Tokens (in/out)",
  "usage.events.col.cost":   "Cost USD",
  "usage.events.col.latency":"Latency",
  "usage.events.col.agent":  "Agent",
  "usage.filter.providers.all": "все провайдеры",

  // ---- Platform page -------------------------------------------------
  "platform.page.title":     "Платформа",
  "platform.page.subtitle":  "Снэпшот за текущий месяц по всем тенантам",
  "platform.tile.tenants":   "Тенантов",
  "platform.tile.tenants.hint": "активных",
  "platform.tile.users":     "Активных пользователей",
  "platform.tile.users.hint":"во всех тенантах",
  "platform.tile.spent":     "Расход MTD",
  "platform.tile.spent.hint":"сумма по всем",
  "platform.tile.period":    "Период",
  "platform.tenants.title":  "Тенанты",
  "platform.tenants.empty":  "Нет тенантов.",
  "platform.col.slug":       "Slug",
  "platform.col.name":       "Название",
  "platform.col.status":     "Статус",
  "platform.col.users":      "Пользователей",
  "platform.col.spend":      "Расход MTD",
  "platform.col.hard_cap":   "Hard cap",
  "platform.col.created":    "Создан",
  "platform.action.suspend":   "Заморозить",
  "platform.action.activate":  "Активировать",
  "platform.row.toggle":               "Развернуть здоровье тенанта",
  "platform.health.never":             "никогда",
  "platform.health.last_login":        "Последний вход",
  "platform.health.last_conversation": "Последний диалог",
  "platform.health.spend":             "Расход",
  "platform.health.projected":         "→ ${projected} прогноз",
  "platform.health.connectors":        "Коннекторы (здоровых / всего)",
  "platform.health.locale_hint":       "локаль: {locale}",
  "platform.health.overall.healthy":          "Здоров",
  "platform.health.overall.needs_attention":  "Требует внимания",

  // ---- Login --------------------------------------------------------
  "login.title":                 "Вход в систему",
  "login.subtitle":              "Авторизуйтесь, чтобы открыть AI Command Center",
  "login.field.email":           "Email",
  "login.field.password":        "Пароль",
  "login.field.tenant":          "Tenant slug (опционально)",
  "login.field.tenant.hint":     "Оставьте пустым, если у вас один tenant",
  "login.field.tenant.placeholder":   "acme",
  "login.submit":                "Войти",
  "login.sso":                   "Войти через корпоративный SSO",
  "login.sso.via":               "Через",
  "login.dev.badge":             "DEV",
  "login.dev.hint":              "Доступно только в development-режиме",
  "login.dev.submit":            "Войти как dev-admin (demo tenant)",
  "login.dev.quick.title":       "Быстрый вход для разработчиков",
  "login.dev.quick.hint":        "Демо-аккаунты с типовыми правами. Только для dev-окружения.",
  "login.dev.role.admin.name":      "Анна Кузнецова",
  "login.dev.role.admin.label":     "Администратор",
  "login.dev.role.dispatcher.name": "Игорь Соколов",
  "login.dev.role.dispatcher.label": "Диспетчер",
  "login.dev.role.operator.name":   "Мария Петрова",
  "login.dev.role.operator.label":  "Оператор",
  "login.dev.role.viewer.name":     "Денис Волков",
  "login.dev.role.viewer.label":    "Наблюдатель",
  "login.dev.role.scopes":          "{count} прав",
  "login.footer":                "Все API-вызовы используют JWT-токен из этой сессии. Tenant определяется токеном, а не URL.",
  // ---- API error identifiers (Sprint EE, ADR 0014 §2) ---------------
  // Backend `HTTPException(detail="...")` payloads are stable English
  // snake_case identifiers; admin-ui maps them to user-facing text
  // here. Anything unmapped falls back to the raw identifier — visible
  // but ugly, which is the desired pressure to add a translation.
  "error.invalid_credentials":           "Неверный логин или пароль",
  "error.mfa_required":                  "Требуется код 2FA",
  "error.invalid_mfa_code":              "Неверный код 2FA",
  "error.invalid_refresh_token":         "Сессия истекла, войдите снова",
  "error.refresh_token_expired_or_revoked": "Сессия истекла, войдите снова",
  "error.totp_already_active":           "2FA уже включена",
  "error.no_pending_totp_setup":         "Сначала настройте 2FA через /totp/setup",
  "error.public_signup_disabled":        "Открытая регистрация отключена на этом сервере",
  "error.pdn_consent_required":          "Требуется согласие на обработку персональных данных",
  "error.user_not_found":                "Пользователь не найден",
  "error.invalid_token":                 "Недействительная ссылка",
  "error.token_already_used":            "Ссылка уже использована",
  "error.token_expired":                 "Срок действия ссылки истёк",
  "error.approval_not_found":            "Запрос на согласование не найден",
  "error.approval_already_decided":      "Решение по запросу уже принято: {arg1}",
  "error.approval_expired":              "Срок действия запроса истёк",
  "error.chain_predecessor_not_visible": "Предыдущий шаг цепочки не виден этому тенанту",
  "error.chain_blocked":                 "Шаг #{arg1} заблокирован: предыдущий — {arg2}, должен быть «approved»",
  "error.connector_not_registered":      "Коннектор «{arg1}» не зарегистрирован на этом сервере",
  "error.loadtest_mode_not_enabled":     "loadtest_mode требует LOADTEST_MODE_ENABLED на сервере",
  "error.platform_budget_tripped":       "Платформа временно превысила лимит на AI. Попробуйте через несколько минут.",
  "error.router_not_initialized":        "LLM-роутер ещё инициализируется, повторите запрос",
  "error.audio_source_required":         "Нужен audio_url или audio_b64",
  "error.audio_source_conflict":         "audio_url и audio_b64 взаимоисключают друг друга",

  // ---- Audit log ----------------------------------------------------
  "audit.page.title":            "Журнал аудита",
  "audit.page.subtitle":         "История security-relevant действий: agent.execute, approval.approve, login и т.д.",
  "audit.action.download_csv":   "Скачать CSV",
  "audit.action.preparing_csv":  "Готовим CSV…",
  "audit.csv.tooltip.enabled":   "Скачать журнал по выбранным фильтрам",
  "audit.csv.tooltip.disabled":  "Задайте фильтр или поиск, чтобы выгрузить CSV",
  "audit.search.placeholder":    "Поиск по actor_id / action / target_id…",
  "audit.search.reset":          "Сбросить",
  "audit.empty":                 "Записи по выбранным фильтрам не найдены.",
  "audit.truncated":             "Показано первых {limit}. Уточните фильтр или сделайте CSV-выгрузку для полного результата.",
  "audit.col.when":              "Когда",
  "audit.col.actor":             "Actor",
  "audit.col.action":            "Action",
  "audit.col.target":            "Target",
  "audit.col.result":            "Result",
  "audit.col.ip":                "IP",

  // ---- Listings -----------------------------------------------------
  "listings.page.title":         "Объявления",
  "listings.search.placeholder": "Поиск по тексту / категории",
  // Note: `listings.draft.*` proper definitions live near the
  // top of this block alongside the rest of the listings page
  // keys. Sprint DI-B's sweep canonicalised them there;
  // duplicates here were removed to keep TS happy
  // (TS1117: same name twice on an object literal).

  // ---- Customers ----------------------------------------------------
  "customers.page.title":        "Клиенты",
  "customers.empty.title":       "Список клиентов пуст",
  "customers.empty.description": "Клиенты появятся после первой переписки.",
  "customers.back":              "Назад к списку",

  // ---- Dashboard ----------------------------------------------------
  "dashboard.title":             "Дашборд",
  "dashboard.kpi.revenue":       "Выручка сегодня",
  "dashboard.kpi.published":     "Опубликовано",
  "dashboard.kpi.active":        "Активные сделки",
  "dashboard.kpi.ai_cost":       "Стоимость AI / нед",
  "dashboard.session.title":     "Сессия с AI диспетчером",
  "dashboard.session.subtitle":  "Пример работы Master Orchestrator (sprint 4)",
  "dashboard.activity.title":    "Лента событий",
  "dashboard.map.title":         "Парк на карте",

  // ---- Agent runs ---------------------------------------------------
  "agent_runs.title":            "Запуски агентов",
  "agent_runs.subtitle":         "Полный аудит. Кликните строку — увидите input/output snapshot и LLM-вызовы",
  "agent_runs.empty":            "Запусков пока нет",
  "agent_runs.input":            "Input snapshot",
  "agent_runs.output":           "Output snapshot",
  "agent_runs.col.time":         "Время",
  "agent_runs.col.agent":        "Агент",
  "agent_runs.col.trigger":      "Триггер",
  "agent_runs.col.status":       "Статус",
  "agent_runs.col.duration":     "Длит.",
  "agent_runs.back":             "Назад",
  "agent_runs.detail.triggered_by": "Triggered by",
  "agent_runs.detail.started":   "Начало",
  "agent_runs.detail.finished":  "Конец",
  "agent_runs.detail.duration":  "Длительность",
  "agent_runs.detail.error":     "Ошибка",

  // ---- Settings -----------------------------------------------------
  "settings.title":              "Учётные данные коннекторов",
  "settings.empty.title":        "Нет коннекторов",
  "settings.empty.description":  "Этот OS-instance не загрузил ни одного коннектора.",

  // ---- Connectors ---------------------------------------------------
  "connectors.title":            "Коннекторы",
  "connectors.subtitle":         "Установленные коннекторы и состояние их синхронизации",
  "connectors.empty.title":      "Нет установленных коннекторов",
  "connectors.empty.description":"Установите коннектор через POST /v1/connectors/install.",

  // ---- Cost dashboard -----------------------------------------------
  "cost.title":                  "Стоимость AI",
  "cost.subtitle":               "Расходы на LLM-вызовы по тенанту, разбивка по провайдерам / агентам / задачам",
  "cost.daily.title":            "Динамика по дням",
  "cost.by_provider":            "По провайдеру",
  "cost.by_agent":               "По агенту",
  "cost.by_task_type":           "По типу задачи",

  // ---- Vehicles -----------------------------------------------------
  "vehicles.title":              "Парк техники",
  "vehicles.subtitle":           "Список техники с актуальной телематикой. Подписан на live-события через WebSocket.",
  "vehicles.empty.title":        "Техника не найдена",
  "vehicles.empty.description":  "Добавьте машины через POST /v1/vehicles или подключите телематик-коннектор.",
  "vehicles.col.vehicle":        "Машина",
  "vehicles.col.type":           "Тип",
  "vehicles.col.plate":          "Гос. номер",
  "vehicles.col.coords":         "Координаты",
  "vehicles.col.speed":          "Скорость",
  "vehicles.col.last_ping":      "Last ping",
  "vehicles.col.status":         "Статус",

  // ---- Conversations ------------------------------------------------
  "conv.sim.title":              "Симулировать входящее сообщение",
  "conv.sim.subtitle":           "CRM Agent создаст Customer + Conversation + автоответ",
  "conv.sim.new_session":        "Новая сессия",
  "conv.sim.field.text":         "Текст сообщения",
  "conv.sim.field.thread":       "Telegram thread id",
  "conv.sim.field.user":         "Telegram user id",
  "conv.sim.send":               "Отправить как входящее",
  "conv.sim.accepted":           "CRM Agent взял в работу. correlation_id: {cid}. Список обновится через ~1 секунду.",
  "conv.list.title":             "Клиенты с диалогами",
  "conv.list.subtitle":          "Откройте клиента, чтобы посмотреть его сообщения",
  "conv.list.empty.title":       "Диалогов пока нет",
  "conv.list.empty.description": "Используйте форму выше — отправьте сообщение, чтобы CRM Agent создал первый диалог.",
  "conv.thread.empty":           "В диалоге пока нет сообщений",
  "conv.composer.label":         "Ответ оператора",
  "conv.composer.placeholder":   "Напишите ответ от лица оператора...",
  "conv.composer.send":          "Отправить",

  // ---- Dispatch map -------------------------------------------------
  "dispatch.title":              "Диспетчер",
  "dispatch.subtitle":           "Парк в реальном времени",
  "dispatch.filter.all":         "Все",
  "dispatch.filter.idle":        "Простаивают",
  "dispatch.filter.busy":        "В работе",
  "dispatch.filter.offline":     "Offline",
  "dispatch.filter.maintenance": "Сервис",
  "dispatch.legend":             "Легенда",
  "dispatch.counts.title":       "Состояние парка",
  "dispatch.counts.total":       "Всего",
  "dispatch.counts.busy":        "В работе",
  "dispatch.counts.idle":        "Простаивают",
  "dispatch.counts.offline":     "Offline",
  "dispatch.counts.maintenance": "Сервис",
  "dispatch.counts.no_gps":      "Без GPS",
  "dispatch.detail.status":      "Статус",
  "dispatch.detail.coords":      "Координаты",
  "dispatch.detail.speed":       "Скорость",
  "dispatch.detail.heading":     "Курс",
  "dispatch.detail.no_data":     "нет данных",
  "dispatch.filter.label":       "Фильтр статуса",

  // ---- Command Center -----------------------------------------------
  "cc.title":                    "AI Command Center",
  "cc.subtitle":                 "Опишите задачу одной фразой. AI поймёт, спланирует и выполнит.",
  "cc.placeholder":              "Напр. «нужен экскаватор 30т Hyundai в Краснодаре»",
  "cc.submit":                   "Запустить Dispatcher",
  "cc.samples_hint":             "или попробуйте:",
  "cc.plan.title":               "План выполнения",
  "cc.plan.loading":             "Жду плана от Dispatcher…",
  "cc.plan.intent_label":        "Намерение",
  "cc.intent.rent_equipment":    "аренда техники",
  "cc.intent.price_inquiry":     "запрос цены",
  "cc.intent.publish_listing":   "публикация объявления",
  "cc.intent.support_question":  "вопрос в поддержку",
  "cc.intent.unclear":           "не понял",
  "cc.activity.title":           "Активность агентов",
  "cc.activity.subtitle":        "Realtime лента событий + последние запуски",
  "cc.activity.history":         "История запусков",
  "cc.activity.feed":            "Лента событий",
  "cc.activity.empty":           "Пока ничего не происходило. Запустите задачу слева.",
  "cc.feed.waiting":             "Подключено. Жду события…",
  "cc.feed.offline":             "WebSocket не подключён.",

  // ---- Inbox --------------------------------------------------------
  "inbox.filter.pending":        "Ожидают",
  "inbox.filter.approved":       "Одобрено",
  "inbox.filter.executed":       "Выполнено",
  "inbox.filter.rejected":       "Отклонено",
  "inbox.filter.all":            "Все",
  "inbox.empty":                 "Здесь пусто. Запустите задачу — AI предложит действия для одобрения.",
  "inbox.placeholder.select":    "Выберите согласование слева",
  "inbox.badge.escalation":      "ЭСКАЛАЦИЯ",
  "inbox.badge.modified":        "изменён",
  "inbox.badge.expired":         "истёк",
  "inbox.expires_in":            "истечёт через {hours}ч",
  "inbox.comment.placeholder":   "Комментарий (опционально)",
};


const EN: Dict = {
  "common.refresh":       "Refresh",
  "common.save":          "Save",
  "common.cancel":        "Cancel",
  "common.saving":        "Saving…",
  "common.loading":       "Loading…",
  "common.no_data":       "No data",
  "common.logout":        "Log out",
  "common.logout.title":  "Log out ({user})",
  "shell.notimpl.title":  "Section {path} is not yet wired up",
  "shell.notimpl.body":   "Coming in a future sprint. Currently available: AI Command, Dashboard, Listings, Conversations, Customers, Agent runs.",
  "shell.ai.title":       "AI assistant",
  "shell.ai.hint":        "The main AI Command Center is in the sidebar (✨ tab)",
  "shell.ai.realtime":    "Realtime chat with agents lands in Sprint 6 (ws-gateway).",
  "shell.ai.usefor":      "For now, use AI Command to launch tasks.",
  "connectors.sync":      "Sync",
  "connectors.sync.started": "Started. correlation_id:",
  "connectors.history":   "Run history",
  "connectors.history.empty": "No runs yet.",
  "connectors.history.col.status": "Status",
  "connectors.history.col.started": "Started",
  "connectors.history.refresh": "Refresh history",
  "connectors.badge.enabled":   "enabled",
  "connectors.badge.disabled":  "disabled",
  "connectors.health.healthy":  "healthy",
  "connectors.health.stale":    "stale",
  "connectors.health.degraded": "{n} failure",
  "connectors.health.failing":  "{n} failures",
  "command.sample.1":     "need a 30t Hyundai excavator in Krasnodar",
  "command.sample.2":     "how much is a 25t crane rental",
  "command.sample.3":     "publish a listing for my JCB 3CX",
  "command.sample.4":     "help me set up a Telegram bot",
  "command.final_answer": "Final answer",
  "command.found":        "Found: {total}, showing top {shown}",
  "conv.sample.text":     "How much is a 30t excavator per shift?",
  "conv.back":            "Back",
  "conv.title":           "Conversation in {channel}",
  "conv.return_ai":       "Return to AI",
  "conv.take_over":       "Take over",
  "customers.kv.created": "Created",
  "customers.related":    "Related entities",
  "customers.related.note": "CRM Agent already records click-throughs — there's just no join page yet.",
  "states.loading":       "Loading...",
  "states.error.title":   "Failed to load",
  "states.error.retry":   "Retry",
  "skeleton.aria.table":  "Loading table",
  "skeleton.aria.stats":  "Loading stats",
  "theme.option.light":   "Light",
  "theme.option.system":  "System",
  "theme.option.dark":    "Dark",
  "theme.aria":           "Theme",
  "errboundary.title":    "Something went wrong",
  "errboundary.scope":    " on page {scope}",
  "errboundary.unknown":  "Unknown error.",
  "errboundary.devnote":  "Diagnostic info has been copied to the browser console.",
  "errboundary.retry":    "Retry",
  "errboundary.reload":   "Reload page",
  "pager.prev":           "← Prev",
  "pager.next":           "Next →",
  "inbox.title":          "Approval inbox",
  "inbox.subtitle":       "AI proposes — you decide. Live updates over WebSocket.",
  "inbox.proposed_by":    "by {agent} ·",
  "inbox.section.reasoning": "AI reasoning",
  "inbox.section.changes":   "What will change",
  "inbox.section.rollback":  "Rollback plan",
  "inbox.section.decision":  "Decision",
  "inbox.editor.toggle":  "Edit payload",
  "inbox.editor.hint":    "Changes will be sent to the agent as `modified_payload`.",
  "inbox.editor.reset":   "Reset",
  "inbox.editor.error.object": "payload must be a JSON object",
  "inbox.editor.error.invalid": "invalid JSON",
  "inbox.action.approve":          "Approve",
  "inbox.action.approve_modified": "Approve with changes",
  "inbox.action.reject":           "Reject",
  "inbox.chain.step":      "Step {n}",
  "inbox.chain.waiting":   "{label} · waiting on previous",
  "inbox.chain.ready":     "{label} · ready for decision",
  "dashboard.header.date":  "May 10 2026 · updated 14:23",
  "dashboard.kpi.trend.revenue":   "+12% vs. avg",
  "dashboard.kpi.trend.published": "+2 new",
  "dashboard.kpi.trend.active":    "all running",
  "dashboard.kpi.trend.ai_cost":   "−18% vs. last",
  "dashboard.demo.q1":       "need a 30t excavator, Krasnodar, 2 weeks",
  "dashboard.demo.region":   "Krasnodar",
  "dashboard.demo.summary":  "Got it. Found 14 options, top-3:",
  "dashboard.demo.l1.spec":  "18 000 ₽/shift · 4 km from site · available from May 12",
  "dashboard.demo.l1.badge": "4.9 · 127 deals",
  "dashboard.demo.l2.spec":  "17 500 ₽/shift · 12 km from site",
  "dashboard.demo.l2.badge": "4.8 · 89 deals",
  "dashboard.demo.l3.spec":  "19 000 ₽/shift · 8 km from site",
  "dashboard.demo.l3.badge": "5.0 · available from May 13",
  "dashboard.demo.book":     "Book #1",
  "dashboard.demo.compare":  "Compare",
  "dashboard.demo.more":     "Show 11 more",
  "dashboard.demo.q2":       "book #1 for 2 weeks starting May 12",
  "dashboard.demo.confirm":  "Ready to book Hyundai R300LC-9SH from May 12 to May 26 (14 shifts × 18 000 ₽ = 252 000 ₽).",
  "dashboard.gate.title":    "Transfer 252 000 ₽ to escrow",
  "dashboard.gate.kv.deal":     "Deal",
  "dashboard.gate.kv.supplier": "Supplier",
  "dashboard.gate.kv.supplier.v": "IE Petrov (4.9★, 127 deals)",
  "dashboard.gate.kv.period":   "Period",
  "dashboard.gate.kv.period.v": "May 12 – May 26, 2026 · 14 shifts",
  "dashboard.gate.kv.amount":   "Amount",
  "dashboard.gate.kv.amount.v": "252 000 ₽ · VAT included",
  "dashboard.gate.kv.deposit":  "Deposit",
  "dashboard.gate.kv.deposit.v": "50 000 ₽ · refund after return",
  "dashboard.gate.risk":     "Human confirmation required for any money operation above 50 000 ₽.",
  "dashboard.gate.primary":  "Confirm and pay",
  "dashboard.gate.secondary":"Change dates",
  "dashboard.gate.confidence_source": "real available slots from supplier",
  "dashboard.activity.all":     "All",
  "dashboard.activity.item1":   "Posting Agent published «Hyundai R300» in Krasnodar. Already 12 views.",
  "dashboard.activity.item2":   "CRM Agent replied to inquiry #ZK-918 (category: 30t).",
  "dashboard.activity.item3":   "Awaiting confirmation: 187 400 ₽ payment → IE Petrov (deal #SD-9182).",
  "dashboard.activity.item3.approve": "Approve",
  "dashboard.activity.item3.reject":  "Reject",
  "dashboard.activity.item4":   "Search Agent processed 4 inquiries in the last hour.",
  "dashboard.map.open":     "Open",
  "dashboard.map.placeholder": "MapLibre lands in Sprint 4 (packages/maps)",
  "dashboard.map.available": "Available: 12",
  "dashboard.map.on_deal":   "On deal: 3",
  "usage.cap.soft.label":  "Soft cap (USD)",
  "usage.cap.hard.label":  "Hard cap (USD)",
  "usage.events.unit.ms":  "ms",
  "listings.draft.title":  "Publish via AI",
  "listings.draft.subtitle": "Photo URL → Posting Agent recognises + creates Listing",
  "listings.draft.url":    "Photo URL",
  "listings.draft.hint":   "Hint (optional)",
  "listings.draft.hint.placeholder": "30t Hyundai R300 excavator",
  "listings.draft.submit": "Run Posting Agent",
  "listings.empty.title":  "No listings yet",
  "listings.empty.body":   "Paste a photo URL above — Posting Agent will create the first one.",
  "listings.col.title":    "Title",
  "listings.col.type":     "Type",
  "listings.col.status":   "Status",
  "listings.col.photos":   "Photos",
  "listings.col.views":    "Views",
  "listings.col.created":  "Created",
  "settings.desc.line1":      "This is where you paste API keys, bot tokens and base URLs.",
  "settings.desc.line2.pre":  "All secrets are ",
  "settings.desc.line2.bold": "encrypted server-side",
  "settings.desc.line2.post": " before being written to the DB (envelope encryption, Sprint P).",
  "settings.feedback.saved":     "Saved. Secrets encrypted and written to the DB.",
  "settings.confirm.uninstall":  "Uninstall «{name}»? All stored keys will be erased (you'll need to enter them again).",
  "settings.feedback.removed":   "Installation removed.",
  "settings.badge.installed":    "installed",
  "settings.badge.disabled":     "disabled",
  "settings.badge.not_installed":"not installed",
  "settings.no_creds":           "This connector requires no credentials.",
  "settings.enabled":            "Enabled",
  "settings.uninstall":          "Uninstall",
  "settings.save_changes":       "Save changes",
  "settings.install":            "Install",
  "settings.field.secret":       "(secret)",
  "settings.field.filled":       "filled",
  "settings.field.placeholder.configured": "•••••••••• (leave empty to keep unchanged)",
  "cost.range.7":      "7d",
  "cost.range.30":     "30d",
  "cost.range.90":     "90d",
  "cost.range.365":    "365d",
  "cost.tile.spend":   "Spend",
  "cost.tile.spend.secondary":  "{calls} calls",
  "cost.tile.tokens":  "Tokens",
  "cost.tile.tokens.secondary": "avg {avg}/call",
  "cost.tile.latency": "Latency",
  "cost.tile.latency.value":    "{ms} ms",
  "cost.tile.latency.secondary":"average",
  "cost.tile.cache.good": "good",
  "cost.tile.cache.low":  "low",
  "cost.empty.period": "No data for the selected period.",
  "cost.chart.tip":    "{day}: ${cost} ({calls} calls)",
  "cost.chart.peak":   "peak: ${cost}",
  "cost.empty.short":  "no data",
  "vehicles.filter.all":         "All",
  "vehicles.filter.idle":        "Idle",
  "vehicles.filter.busy":        "Busy",
  "vehicles.filter.maintenance": "Service",
  "vehicles.filter.offline":     "Offline",
  "vehicles.empty.short":  "no data",
  "vehicles.unit.kmh":     "km/h",
  "vehicles.ago.just_now": "just now",
  "vehicles.ago.seconds":  "{s}s",
  "vehicles.ago.minutes":  "{m}m",
  "vehicles.ago.hours":    "{h}h",
  "vehicles.ago.days":     "{d}d",
  "dispatch.map.aria":     "Dispatcher map",

  "nav.command":          "AI Command",
  "nav.inbox":            "Inbox",
  "nav.dashboard":        "Dashboard",
  "nav.listings":         "Listings",
  "nav.customers":        "Customers",
  "nav.messages":         "Conversations",
  "nav.fleet":            "Fleet",
  "nav.dispatch":         "Dispatch",
  "nav.orders":           "Orders",
  "nav.intake":           "Intake funnel",
  "nav.alerts":           "Alerts",
  "alerts.page.title":    "Vehicle alerts",
  "alerts.empty.title":   "No alerts",
  "alerts.empty.unack":   "All alerts handled. Nice.",
  "alerts.empty.any":     "Wialon hasn't fired any alerts yet.",
  "alerts.tab.unack":     "Unacknowledged",
  "alerts.tab.acked":     "Acknowledged",
  "alerts.tab.all":       "All",
  "alerts.rule.geofence_exit": "Left geofence",
  "alerts.rule.idle_too_long": "Idle too long",
  "alerts.rule.fuel_drop":     "Fuel drop",
  "alerts.rule.speed_over":    "Speeding",
  "alerts.row.vehicle":   "vehicle #{id}",
  "alerts.row.ack":       "Acknowledge",
  "alerts.row.acked_at":  "acked {ts}",
  "alerts.details.zone":   "zone",
  "alerts.details.elapsed":"idle {mins} min",
  "alerts.details.since":  "since",
  "orders.page.title":    "Orders",
  "orders.empty.title":   "No orders yet",
  "orders.empty.description": "Connect ATI.SU or 1С — orders will appear here after the first pull.",
  "orders.tab.all":           "All",
  "orders.tab.new":           "New",
  "orders.tab.accepted":      "Accepted",
  "orders.tab.in_progress":   "In progress",
  "orders.tab.completed":     "Completed",
  "orders.status.new":         "new",
  "orders.status.accepted":    "accepted",
  "orders.status.declined":    "declined",
  "orders.status.in_progress": "in progress",
  "orders.status.completed":   "completed",
  "orders.status.cancelled":   "cancelled",
  "orders.detail.title":       "Order",
  "orders.detail.route":       "Route",
  "orders.detail.sources":     "Sources ({n})",
  "orders.detail.metadata":    "Extra data",
  "orders.detail.kg":          "kg",
  "orders.detail.first_seen":  "first seen",
  "orders.detail.last_seen":   "last seen",
  "orders.detail.updated_at":  "updated {ts}",
  "orders.detail.terminal":    "Terminal status — no transitions available",
  "nav.agent_runs":       "Agent runs",
  "nav.connectors":       "Connectors",
  "nav.cost":             "AI cost",
  "nav.usage":            "Usage & quota",
  "nav.platform":         "Platform",
  "nav.audit":            "Audit log",
  "nav.analytics":        "Analytics",
  "nav.settings":         "Settings",
  "nav.privacy":          "Privacy",

  "privacy.page.title":          "Privacy & consent",
  "privacy.page.subtitle":       "Personal data processing policy and your 152-ФЗ rights",
  "privacy.tile.consent":        "Consent",
  "privacy.tile.consent.active": "Active",
  "privacy.tile.consent.refresh":"Refresh required",
  "privacy.tile.consent.none":   "Not given",
  "privacy.tile.deletion":       "Account deletion",
  "privacy.tile.deletion.none":  "Not requested",
  "privacy.tile.deletion.pending":"Requested",
  "privacy.action.accept":       "Accept policy",
  "privacy.action.delete":       "Delete account",
  "privacy.action.cancel":       "Cancel deletion",
  "privacy.banner.text":         "A new version of the privacy policy has been published. Please confirm consent to continue.",
  "privacy.banner.read":         "Read",
  "privacy.banner.accept":       "Accept",
  "privacy.confirm.delete":      "Request account deletion?\n\nPersonal data will be depersonalised in 30 days. You can cancel any time before then.",
  "privacy.tile.consent.secondary":   "version {version} · {date}",
  "privacy.tile.consent.secondary.noversion": "—",
  "privacy.tile.deletion.fires":      "depersonalisation {date}",
  "privacy.tile.deletion.empty":      "You can request below",
  "privacy.tile.version":             "Current policy version",
  "privacy.tile.version.hint":        "below on this page",
  "privacy.tile.email":               "Email",
  "privacy.tile.email.verified":      "verified",
  "privacy.tile.email.unverified":    "not verified",

  "usage.page.title":     "AI usage & limits",
  "usage.page.subtitle":  "Current period, spend, soft / hard cap",
  "usage.edit_cap":       "Edit cap",
  "usage.tile.spent":     "Spent this period",
  "usage.tile.spent.ok":  "within limits",
  "usage.tile.spent.over_soft": "soft cap exceeded",
  "usage.tile.spent.over_hard": "hard cap exceeded",
  "usage.tile.soft":      "Soft cap",
  "usage.tile.soft.hint": "alert only",
  "usage.tile.hard":      "Hard cap",
  "usage.tile.hard.hint": "blocks LLM calls",
  "usage.tile.period":    "Period",
  "usage.tile.period.hint": "UTC",
  "usage.cap.soft.hint":  "Alert-only, never blocks. Blank = no soft cap.",
  "usage.cap.hard.hint":  "Blocks AI calls when exceeded. Blank = no hard cap.",
  "usage.cap.placeholder": "no limit",
  "usage.forecast.label":      "Month-end projection:",
  "usage.forecast.days":       "({elapsed} of {total} days)",
  "usage.forecast.over_soft":  "will exceed soft cap (${cap})",
  "usage.forecast.over_hard":  "will exceed hard cap (${cap}) — AI calls will be blocked",
  "usage.forecast.day1_hint":  "(day-1 projection — noisy)",
  "usage.forecast.method.linear":     "(linear extrapolation)",
  "usage.forecast.method.median_7d":  "(7-day median)",
  "usage.forecast.linear_was":        "linear was ${value}",
  "settings.test_connection":         "Test connection",
  "settings.test.ok":                 "✓ Connection successful",
  "settings.test.fail":               "✗ Connection failed",
  "usage.forecast.anomaly.label":     "⚠ Spend spike detected today",
  "usage.forecast.anomaly.today":     "today: ${value}",
  "usage.forecast.anomaly.baseline":  "baseline (7-day median): ${value}",
  "usage.events.title":   "Recent LLM calls",
  "usage.events.col.time":   "Time",
  "usage.events.col.model":  "Provider · Model",
  "usage.events.col.task":   "Task",
  "usage.events.col.tokens": "Tokens (in/out)",
  "usage.events.col.cost":   "Cost USD",
  "usage.events.col.latency":"Latency",
  "usage.events.col.agent":  "Agent",
  "usage.filter.providers.all": "all providers",

  "platform.page.title":     "Platform",
  "platform.page.subtitle":  "MTD snapshot across every tenant",
  "platform.tile.tenants":   "Tenants",
  "platform.tile.tenants.hint": "active",
  "platform.tile.users":     "Active users",
  "platform.tile.users.hint":"across tenants",
  "platform.tile.spent":     "Spend MTD",
  "platform.tile.spent.hint":"all tenants",
  "platform.tile.period":    "Period",
  "platform.tenants.title":  "Tenants",
  "platform.tenants.empty":  "No tenants yet.",
  "platform.col.slug":       "Slug",
  "platform.col.name":       "Name",
  "platform.col.status":     "Status",
  "platform.col.users":      "Users",
  "platform.col.spend":      "Spend MTD",
  "platform.col.hard_cap":   "Hard cap",
  "platform.col.created":    "Created",
  "platform.action.suspend":   "Suspend",
  "platform.action.activate":  "Activate",
  "platform.row.toggle":               "Expand tenant health",
  "platform.health.never":             "never",
  "platform.health.last_login":        "Last login",
  "platform.health.last_conversation": "Last conversation",
  "platform.health.spend":             "Spend",
  "platform.health.projected":         "→ ${projected} projected",
  "platform.health.connectors":        "Connectors (healthy / total)",
  "platform.health.locale_hint":       "locale: {locale}",
  "platform.health.overall.healthy":          "Healthy",
  "platform.health.overall.needs_attention":  "Needs attention",

  "login.title":                 "Sign in",
  "login.subtitle":              "Authenticate to open the AI Command Center",
  "login.field.email":           "Email",
  "login.field.password":        "Password",
  "login.field.tenant":          "Tenant slug (optional)",
  "login.field.tenant.hint":     "Leave empty if you have a single tenant",
  "login.field.tenant.placeholder":   "acme",
  "login.submit":                "Sign in",
  "login.sso":                   "Sign in with corporate SSO",
  "login.sso.via":               "via",
  "login.dev.badge":             "DEV",
  "login.dev.hint":              "Available only in development mode",
  "login.dev.submit":            "Sign in as dev-admin (demo tenant)",
  "login.dev.quick.title":       "Quick sign-in for developers",
  "login.dev.quick.hint":        "Demo accounts with role-typical permissions. Dev only.",
  "login.dev.role.admin.name":      "Anna Kuznetsova",
  "login.dev.role.admin.label":     "Administrator",
  "login.dev.role.dispatcher.name": "Igor Sokolov",
  "login.dev.role.dispatcher.label": "Dispatcher",
  "login.dev.role.operator.name":   "Maria Petrova",
  "login.dev.role.operator.label":  "Operator",
  "login.dev.role.viewer.name":     "Denis Volkov",
  "login.dev.role.viewer.label":    "Viewer",
  "login.dev.role.scopes":          "{count} scopes",
  "login.footer":                "Every API call uses the JWT from this session. Tenant is taken from the token, not the URL.",
  // ---- API error identifiers (Sprint EE, ADR 0014 §2) ---------------
  "error.invalid_credentials":           "Invalid email or password",
  "error.mfa_required":                  "2FA code required",
  "error.invalid_mfa_code":              "Invalid 2FA code",
  "error.invalid_refresh_token":         "Session expired, please sign in again",
  "error.refresh_token_expired_or_revoked": "Session expired, please sign in again",
  "error.totp_already_active":           "2FA is already enabled",
  "error.no_pending_totp_setup":         "Set up 2FA first via /totp/setup",
  "error.public_signup_disabled":        "Public signup is disabled on this deployment",
  "error.pdn_consent_required":          "Consent to personal data processing is required",
  "error.user_not_found":                "User not found",
  "error.invalid_token":                 "Invalid link",
  "error.token_already_used":            "Link already used",
  "error.token_expired":                 "Link has expired",
  "error.approval_not_found":            "Approval request not found",
  "error.approval_already_decided":      "Approval has already been decided: {arg1}",
  "error.approval_expired":              "Approval request has expired",
  "error.chain_predecessor_not_visible": "Previous chain step is not visible to this tenant",
  "error.chain_blocked":                 "Step #{arg1} is blocked: predecessor is {arg2}, must be «approved»",
  "error.connector_not_registered":      "Connector «{arg1}» is not registered on this instance",
  "error.loadtest_mode_not_enabled":     "loadtest_mode requires LOADTEST_MODE_ENABLED on the server",
  "error.platform_budget_tripped":       "The platform AI budget is temporarily exceeded. Please retry in a few minutes.",
  "error.router_not_initialized":        "LLM router is still initialising, retry shortly",
  "error.audio_source_required":         "audio_url or audio_b64 is required",
  "error.audio_source_conflict":         "audio_url and audio_b64 are mutually exclusive",

  "audit.page.title":            "Audit log",
  "audit.page.subtitle":         "Security-relevant action history: agent.execute, approval.approve, login, …",
  "audit.action.download_csv":   "Download CSV",
  "audit.action.preparing_csv":  "Preparing CSV…",
  "audit.csv.tooltip.enabled":   "Download the journal for the selected filters",
  "audit.csv.tooltip.disabled":  "Set a filter or search to export CSV",
  "audit.search.placeholder":    "Search by actor_id / action / target_id…",
  "audit.search.reset":          "Reset",
  "audit.empty":                 "No entries match the selected filters.",
  "audit.truncated":             "Showing first {limit}. Refine the filter or export to CSV for the full result.",
  "audit.col.when":              "When",
  "audit.col.actor":             "Actor",
  "audit.col.action":            "Action",
  "audit.col.target":            "Target",
  "audit.col.result":            "Result",
  "audit.col.ip":                "IP",

  "listings.page.title":         "Listings",
  "listings.search.placeholder": "Search by text / category",
  // See RU block for the listings.draft.* note — canonical
  // defs live near the top alongside the rest of the listings
  // keys; duplicates here removed.

  "customers.page.title":        "Customers",
  "customers.empty.title":       "No customers yet",
  "customers.empty.description": "Customers appear after the first conversation.",
  "customers.back":              "Back to list",

  "dashboard.title":             "Dashboard",
  "dashboard.kpi.revenue":       "Revenue today",
  "dashboard.kpi.published":     "Published",
  "dashboard.kpi.active":        "Active deals",
  "dashboard.kpi.ai_cost":       "AI cost / week",
  "dashboard.session.title":     "AI dispatcher session",
  "dashboard.session.subtitle":  "Master Orchestrator example (sprint 4)",
  "dashboard.activity.title":    "Activity feed",
  "dashboard.map.title":         "Fleet on the map",

  "agent_runs.title":            "Agent runs",
  "agent_runs.subtitle":         "Full audit. Click a row to see the input/output snapshot and LLM calls",
  "agent_runs.empty":            "No runs yet",
  "agent_runs.input":            "Input snapshot",
  "agent_runs.output":           "Output snapshot",
  "agent_runs.col.time":         "Time",
  "agent_runs.col.agent":        "Agent",
  "agent_runs.col.trigger":      "Trigger",
  "agent_runs.col.status":       "Status",
  "agent_runs.col.duration":     "Dur.",
  "agent_runs.back":             "Back",
  "agent_runs.detail.triggered_by": "Triggered by",
  "agent_runs.detail.started":   "Started",
  "agent_runs.detail.finished":  "Finished",
  "agent_runs.detail.duration":  "Duration",
  "agent_runs.detail.error":     "Error",

  "settings.title":              "Connector credentials",
  "settings.empty.title":        "No connectors",
  "settings.empty.description":  "This OS instance has no connectors loaded.",

  "connectors.title":            "Connectors",
  "connectors.subtitle":         "Installed connectors and their sync status",
  "connectors.empty.title":      "No connectors installed",
  "connectors.empty.description":"Install one via POST /v1/connectors/install.",

  "cost.title":                  "AI cost",
  "cost.subtitle":               "LLM-call spend for this tenant, by provider / agent / task type",
  "cost.daily.title":            "Daily timeline",
  "cost.by_provider":            "By provider",
  "cost.by_agent":               "By agent",
  "cost.by_task_type":           "By task type",

  "vehicles.title":              "Fleet",
  "vehicles.subtitle":           "Fleet inventory with live telemetry. Subscribed to live events via WebSocket.",
  "vehicles.empty.title":        "No vehicles found",
  "vehicles.empty.description":  "Add vehicles via POST /v1/vehicles or hook up a telematics connector.",
  "vehicles.col.vehicle":        "Vehicle",
  "vehicles.col.type":           "Type",
  "vehicles.col.plate":          "Plate",
  "vehicles.col.coords":         "Coords",
  "vehicles.col.speed":          "Speed",
  "vehicles.col.last_ping":      "Last ping",
  "vehicles.col.status":         "Status",

  "conv.sim.title":              "Simulate inbound message",
  "conv.sim.subtitle":           "CRM Agent will create Customer + Conversation + auto-reply",
  "conv.sim.new_session":        "New session",
  "conv.sim.field.text":         "Message text",
  "conv.sim.field.thread":       "Telegram thread id",
  "conv.sim.field.user":         "Telegram user id",
  "conv.sim.send":               "Send as inbound",
  "conv.sim.accepted":           "CRM Agent accepted. correlation_id: {cid}. The list refreshes in ~1 second.",
  "conv.list.title":             "Customers with conversations",
  "conv.list.subtitle":          "Open a customer to see their messages",
  "conv.list.empty.title":       "No conversations yet",
  "conv.list.empty.description": "Use the form above — send a message so CRM Agent creates the first thread.",
  "conv.thread.empty":           "No messages in this conversation yet",
  "conv.composer.label":         "Operator reply",
  "conv.composer.placeholder":   "Write a reply as the operator…",
  "conv.composer.send":          "Send",

  "dispatch.title":              "Dispatch",
  "dispatch.subtitle":           "Fleet in real time",
  "dispatch.filter.all":         "All",
  "dispatch.filter.idle":        "Idle",
  "dispatch.filter.busy":        "Busy",
  "dispatch.filter.offline":     "Offline",
  "dispatch.filter.maintenance": "Maintenance",
  "dispatch.legend":             "Legend",
  "dispatch.counts.title":       "Fleet state",
  "dispatch.counts.total":       "Total",
  "dispatch.counts.busy":        "Busy",
  "dispatch.counts.idle":        "Idle",
  "dispatch.counts.offline":     "Offline",
  "dispatch.counts.maintenance": "Maintenance",
  "dispatch.counts.no_gps":      "No GPS",
  "dispatch.detail.status":      "Status",
  "dispatch.detail.coords":      "Coords",
  "dispatch.detail.speed":       "Speed",
  "dispatch.detail.heading":     "Heading",
  "dispatch.detail.no_data":     "no data",
  "dispatch.filter.label":       "Status filter",

  "cc.title":                    "AI Command Center",
  "cc.subtitle":                 "Describe a task in one sentence. The AI understands, plans, and executes.",
  "cc.placeholder":              "e.g. \"need a 30 t Hyundai excavator in Krasnodar\"",
  "cc.submit":                   "Run Dispatcher",
  "cc.samples_hint":             "or try:",
  "cc.plan.title":               "Execution plan",
  "cc.plan.loading":             "Waiting for plan from Dispatcher…",
  "cc.plan.intent_label":        "Intent",
  "cc.intent.rent_equipment":    "equipment rental",
  "cc.intent.price_inquiry":     "price inquiry",
  "cc.intent.publish_listing":   "publish listing",
  "cc.intent.support_question":  "support question",
  "cc.intent.unclear":           "unclear",
  "cc.activity.title":           "Agent activity",
  "cc.activity.subtitle":        "Realtime event feed + recent runs",
  "cc.activity.history":         "Run history",
  "cc.activity.feed":            "Event feed",
  "cc.activity.empty":           "Nothing has happened yet. Launch a task on the left.",
  "cc.feed.waiting":             "Connected. Waiting for events…",
  "cc.feed.offline":             "WebSocket not connected.",

  "inbox.filter.pending":        "Pending",
  "inbox.filter.approved":       "Approved",
  "inbox.filter.executed":       "Executed",
  "inbox.filter.rejected":       "Rejected",
  "inbox.filter.all":            "All",
  "inbox.empty":                 "Empty inbox. Launch a task — the AI will propose actions to approve.",
  "inbox.placeholder.select":    "Select an approval on the left",
  "inbox.badge.escalation":      "ESCALATED",
  "inbox.badge.modified":        "modified",
  "inbox.badge.expired":         "expired",
  "inbox.expires_in":            "expires in {hours}h",
  "inbox.comment.placeholder":   "Comment (optional)",
};


export const DICTIONARIES: Record<Lang, Dict> = { ru: RU, en: EN };
